/**
 * Chat Search Component
 * Global search for messages and files across all channels and DMs
 */

import React, { useState, useEffect, useRef } from 'react';
import { Input, Drawer, List, Avatar, Typography, Empty, Spin, Tag, Space, Button } from 'antd';
import { SearchOutlined, CloseOutlined, FileOutlined, MessageOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './ChatSearch.css';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

interface SearchResult {
  id: string;
  channelId: string;
  channelName: string;
  channelType: 'group' | 'direct' | 'private';
  channelDisplayName: string;
  userId: string;
  text: string;
  contentType: string;
  attachmentUrl?: string;
  attachmentName?: string;
  matchType: 'text' | 'file';
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ChatSearchProps {
  open: boolean;
  onClose: () => void;
  onSelectChannel?: (channelId: string) => void;
}

export const ChatSearch: React.FC<ChatSearchProps> = ({ open, onClose, onSelectChannel }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const inputRef = useRef<any>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Focus input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, [open]);

  // Fetch search results
  const { data: searchResults, isLoading } = useQuery(
    ['chat-search', debouncedSearchTerm],
    async () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.trim().length < 1) {
        return { messages: [], total: 0, query: '' };
      }
      const res = await api.get('/api/chat/search', {
        params: { q: debouncedSearchTerm.trim(), limit: 50 }
      });
      return res.data;
    },
    {
      enabled: !!debouncedSearchTerm && debouncedSearchTerm.trim().length >= 1,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  const handleResultClick = (result: SearchResult) => {
    if (onSelectChannel) {
      onSelectChannel(result.channelId);
      onClose();
    }
  };

  const formatTime = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY [at] h:mm A');
  };

  const highlightText = (text: string, searchTerm: string) => {
    if (!text || !searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="search-highlight">{part}</mark>
      ) : (
        part
      )
    );
  };

  const getChannelIcon = (channelType: string) => {
    switch (channelType) {
      case 'direct':
        return '💬';
      case 'private':
        return '🔒';
      default:
        return '#';
    }
  };

  const messages = searchResults?.messages || [];
  const hasResults = messages.length > 0;
  const showResults = debouncedSearchTerm.trim().length >= 1;

  return (
    <Drawer
      title={
        <div className="chat-search-header">
          <SearchOutlined style={{ marginRight: 8 }} />
          <span>Search Messages & Files</span>
        </div>
      }
      placement="top"
      height="80vh"
      onClose={onClose}
      open={open}
      className="chat-search-drawer"
      extra={
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      }
    >
      <div className="chat-search-content">
        <Input
          ref={inputRef}
          size="large"
          placeholder="Search messages and files..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          className="chat-search-input"
        />

        {isLoading && (
          <div className="chat-search-loading">
            <Spin size="large" />
          </div>
        )}

        {!isLoading && showResults && !hasResults && (
          <Empty
            description="No results found"
            style={{ marginTop: 60 }}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {!isLoading && showResults && hasResults && (
          <div className="chat-search-results">
            <div className="chat-search-results-header">
              <Text type="secondary">
                Found {searchResults?.total || 0} {searchResults?.total === 1 ? 'result' : 'results'}
              </Text>
            </div>
            <List
              dataSource={messages}
              renderItem={(item: SearchResult) => (
                <List.Item
                  className="chat-search-result-item"
                  onClick={() => handleResultClick(item)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: item.matchType === 'file' ? '#1890ff' : '#52c41a'
                        }}
                        icon={item.matchType === 'file' ? <FileOutlined /> : <MessageOutlined />}
                      />
                    }
                    title={
                      <Space>
                        <Text strong>
                          {item.user.firstName} {item.user.lastName}
                        </Text>
                        <Text type="secondary">in</Text>
                        <Tag color={item.channelType === 'direct' ? 'blue' : item.channelType === 'private' ? 'red' : 'default'}>
                          {getChannelIcon(item.channelType)} {item.channelDisplayName}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatTime(item.createdAt)}
                        </Text>
                      </Space>
                    }
                    description={
                      <div>
                        {item.matchType === 'file' ? (
                          <div>
                            <FileOutlined style={{ marginRight: 4 }} />
                            <Text strong>{item.attachmentName}</Text>
                            {item.text && (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary" ellipsis>
                                  {highlightText(item.text, debouncedSearchTerm)}
                                </Text>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Paragraph
                            ellipsis={{ rows: 2, expandable: false }}
                            style={{ marginBottom: 0 }}
                          >
                            {highlightText(item.text, debouncedSearchTerm)}
                          </Paragraph>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}

        {!showResults && (
          <div className="chat-search-empty-state">
            <SearchOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <Text type="secondary">Start typing to search messages and files</Text>
          </div>
        )}
      </div>
    </Drawer>
  );
};




